// Include CSS for Vite processing (Tailwind + HMR)
import "../css/app.css";

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { hooks as colocatedHooks } from "phoenix-colocated/pci";
import topbar from "../vendor/topbar";
import { ReactComponent } from "./hooks/react_hook";

// React imports — mount components into Phoenix templates by id
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";

const csrfToken = document
  .querySelector("meta[name='csrf-token']")!
  .getAttribute("content")!;

const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: { _csrf_token: csrfToken },
  hooks: { ...colocatedHooks, ReactComponent },
});

// Show progress bar on live navigation and form submits
topbar.config({
  barColors: { 0: "#29d" },
  shadowColor: "rgba(0, 0, 0, .3)",
});
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// connect if there are any LiveViews on the page
liveSocket.connect();

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)
// >> liveSocket.disableLatencySim()
(window as any).liveSocket = liveSocket;

// Phoenix live reload dev features
if (import.meta.env.DEV) {
  window.addEventListener(
    "phx:live_reload:attached",
    ({ detail: reloader }: any) => {
      reloader.enableServerLogs();

      let keyDown: string | null = null;
      window.addEventListener("keydown", (e) => (keyDown = e.key));
      window.addEventListener("keyup", (_e) => (keyDown = null));
      window.addEventListener(
        "click",
        (e) => {
          if (keyDown === "c") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtCaller(e.target);
          } else if (keyDown === "d") {
            e.preventDefault();
            e.stopImmediatePropagation();
            reloader.openEditorAtDef(e.target);
          }
        },
        true
      );

      (window as any).liveReloader = reloader;
    }
  );
}

// Mount React components into designated containers
// Usage: add a <div id="react-{name}"> in any Phoenix template
// and register the component in the map below.
const reactComponents: Record<string, () => Promise<{ default: React.ComponentType }>> = {
  // Example:
  // "react-dashboard": () => import("./components/Dashboard"),
};

function mountReactComponents() {
  for (const [id, loader] of Object.entries(reactComponents)) {
    const el = document.getElementById(id);
    if (el) {
      loader().then(({ default: Component }) => {
        createRoot(el).render(<Component />);
      });
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountReactComponents);
} else {
  mountReactComponents();
}
